/**
 * Attachment Preview Utilities
 *
 * S3 serves every mail attachment as `application/octet-stream`, so a signed URL
 * opened directly downloads rather than previews. The chip therefore fetches the
 * bytes and re-wraps them in a Blob carrying the real content type, then opens
 * that blob in a tab.
 *
 * The blob URL is the only handle the preview tab has on the file: the PDF
 * viewer's own Save, Ctrl+S and right-click → Save all re-read it. Revoking it
 * on a timer leaves the already-painted page readable but every download route
 * out of it dead, which is what this module exists to prevent — the preview
 * document owns its blob URLs and releases them when it is itself unloaded.
 */

/** How long a download's blob URL is kept alive after the click. */
export const DOWNLOAD_BLOB_LIFETIME_MS = 30_000;

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape a value for interpolation into the preview document. Attachment names
 * come from inbound mail, so they are attacker-chosen text, not trusted copy.
 */
export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (character) => HTML_ENTITIES[character]);

interface PreviewDocumentInput {
  /** Blob URL holding the file's bytes, typed as the real content type. */
  readonly fileUrl: string;
  /** The name the file should be saved under. */
  readonly fileName: string;
}

/**
 * Build the wrapper page a previewed attachment opens into: the file in an
 * iframe, plus a Download link that saves it under its real name.
 *
 * The link is a plain anchor on purpose — downloading must not depend on the
 * inline script running, since a blob document may inherit a CSP that blocks it.
 */
export const buildPreviewDocument = ({
  fileUrl,
  fileName,
}: PreviewDocumentInput): string => {
  const safeName = escapeHtml(fileName);

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeName}</title>
    <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23dc2626'><path d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z' /></svg>" />
    <style>
      body { margin: 0; padding: 0; font-family: Arial, sans-serif; display: flex; flex-direction: column; height: 100vh; }
      iframe { flex: 1; width: 100%; border: none; }
      .header { display: flex; align-items: center; gap: 1rem; background: #f5f5f5; padding: 0.5rem 1rem; border-bottom: 1px solid #ddd; font-size: 0.875rem; color: #333; }
      .name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .download { color: #1a56db; text-decoration: none; font-weight: 600; white-space: nowrap; }
      .download:hover { text-decoration: underline; }
    </style>
  </head>
  <body>
    <div class="header">
      <span class="name">${safeName}</span>
      <a class="download" href="${fileUrl}" download="${safeName}">Download</a>
    </div>
    <iframe src="${fileUrl}" title="${safeName}"></iframe>
    <script>
      // Hold the blob until this tab goes away; releasing it earlier is what
      // breaks Download, the viewer's own Save and Ctrl+S.
      addEventListener('pagehide', function () {
        URL.revokeObjectURL(${JSON.stringify(fileUrl)});
        URL.revokeObjectURL(location.href);
      });
    </script>
  </body>
</html>`;
};

/**
 * Fetch an attachment's bytes and return a blob URL typed as `contentType`.
 * The caller owns the URL and must revoke it.
 */
export const fetchAttachmentBlobUrl = async (
  url: string | undefined,
  contentType: string
): Promise<string> => {
  if (!url) {
    throw new Error('This attachment has no stored file.');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`The file store returned ${response.status}.`);
  }

  return URL.createObjectURL(
    new Blob([await response.arrayBuffer()], { type: contentType })
  );
};

/** Save a blob URL to disk under `fileName`, without navigating the page. */
export const triggerDownload = (fileUrl: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking in this same task can cancel a download the browser has not yet
  // started reading; a later task is safe and still bounds the blob's lifetime.
  setTimeout(() => URL.revokeObjectURL(fileUrl), DOWNLOAD_BLOB_LIFETIME_MS);
};
